import AdmicityGroupFilterSelect from '../../../../shared-components/AdmicityGroupFilterSelect';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import React, {useRef, useState} from 'react';
import {Box, Checkbox, FormControlLabel} from '@mui/material';

/* eslint-disable react/prop-types */

// AD-2699 (Maria, 15 October 2026): the box stays open after 'Add', so several groups can be added one after
// another, each going into the table straight away. 'Done' closes it. Used by the Create Form box, Form Settings and the
// Create Report box.
const AddStudentGroupDialog = (
    {
        open,
        onClose,
        groupFilters,
        onChange,
        maxWidth = 'sm',
        boxWidth
    }) => {
    const [filter, setFilter] = useState(null);
    const [excludeGroup, setExcludeGroup] = useState(false);
    const [addedCount, setAddedCount] = useState(0);
    const pickerRef = useRef(null);
    const isFilterNotSelected = filter == null || !filter.filterId;
    const rootFilterIds = groupFilters ? groupFilters.map(x => x.id) : [];

    const handleClose = () => {
        setFilter(null);
        setExcludeGroup(false);
        setAddedCount(0);
        onClose();
    };

    const add = () => {
        onChange({...filter, excluded: excludeGroup});
        setFilter(null);
        setExcludeGroup(false);
        setAddedCount(count => count + 1);
    };

    // Opens the group list, as clicking on the Groups box does.
    const openGroupList = () => pickerRef.current?.querySelector('[data-testid="group-picker"]')?.click();

    return (
        <AdmicityDialog
            title="Add group"
            maxWidth={boxWidth ? 'xs' : maxWidth}
            // Maria, 15 October 2026: inside a box (Create Report, Create Form) the pop-up lines up with the box's
            // right-hand edge, so the table can be seen filling up. 444 is the width of an 'xs' pop-up.
            dialogSx={boxWidth ? {'& .MuiDialog-paper': {position: 'relative', left: {md: (boxWidth - 444) / 2}}} : undefined}
            open={open}
            handleClose={handleClose}
            contentSx={{pb: 0}}
            actionsSx={{px: 3, pt: 1, pb: 2.5, gap: 1}}
            actions={[
                // Maria, 15 October 2026: with no group selected the button is a blue outlined 'Add Group' ('Add Another
                // Group' once one has been added) that opens the group list; with a group selected it is a green 'Add'.
                isFilterNotSelected
                    ? {
                        label: addedCount > 0 ? 'Add Another Group' : 'Add Group',
                        variant: 'outlined',
                        sx: {color: '#186BBE', borderColor: '#186BBE'},
                        onClick: openGroupList
                    }
                    : {
                        label: 'Add',
                        variant: 'contained',
                        sx: {backgroundColor: '#0DAD95', boxShadow: 'none', '&:hover': {backgroundColor: '#0B957F', boxShadow: 'none'}},
                        // Keeps the focus in the Groups box: losing it shrinks the box, 'Add' moved and the first
                        // click missed when the group's name ran onto two lines (walkthrough, 15 October 2026).
                        onMouseDown: (event) => event.preventDefault(),
                        onClick: add
                    },
                {
                    label: 'Done',
                    variant: 'contained',
                    sx: {backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}},
                    onClick: handleClose
                },
            ]}
        >
            <Box
                ref={pickerRef}
                display="flex"
                flexDirection="column"
            >
                <AdmicityGroupFilterSelect
                    sx={{
                        width: '100%',
                        marginTop: 1
                    }}
                    data={groupFilters}
                    value={filter?.filterId || ''}
                    onChange={(filterId, paths) => {
                        setFilter({filterId, paths});
                        setExcludeGroup(false);
                    }}
                />
                <FormControlLabel
                    sx={{
                        marginTop: 1
                    }}
                    control={
                        <Checkbox
                            checked={excludeGroup}
                            disabled={isFilterNotSelected || rootFilterIds.includes(filter.filterId)}
                            onChange={(event) => setExcludeGroup(event.target.checked)}
                        />
                    }
                    label="Exclude group"/>
            </Box>
        </AdmicityDialog>
    );
};

export default AddStudentGroupDialog;
